import {
  dynamicValue,
  filtersToGraphQLFilters,
  IPageFilterProps,
  IProperty,
  IWork,
  makeDynamicReportingPageStore,
  mergeFiltersValues,
  DATA_GRID_STATUS_COLUMN_KEY,
  PageFiltersRenderer,
  performanceWorkFilterOptionsQuery,
  useParams,
  workflowsUtils,
} from '@diana/core';
import { PerformanceReportKey } from '@diana/core/build/queries/performance';
import { useQuery } from '@tanstack/react-query';
import { isString, omit, uniq } from 'lodash';
import { DateTime } from 'luxon';
import { ReactElement, useMemo } from 'react';

export const QuickFilters = ['Port', 'DeliveryDate'];

export const defaultDashboardQueryDateFrom = DateTime.now().minus({ years: 1 }).toISO();
export const defaultDashboardQueryDateTo = DateTime.now().toISO();

export const ReportingFilterRenderer = ({
  filterProps,
  storeId,
}: {
  filterProps: IPageFilterProps;
  storeId?: string;
}) => {
  const {
    filter: { property },
    filtersValues,
  } = filterProps;
  const { reportId } = useParams<{ reportId: string }>();
  const useReportingPageStore = makeDynamicReportingPageStore(reportId! || storeId!);
  const availableFilters = useReportingPageStore((s) => s.availableFilters);
  const flows = useReportingPageStore((store) => store.flows);
  const properties = useReportingPageStore((store) => store.properties);
  const defaultFiltersValues = useReportingPageStore((store) => store.defaultFiltersValues);
  const flowTypes = useMemo(() => workflowsUtils.getUniqueFlowTypes({ flows }), [flows]);
  const flowOriginIds = useMemo(() => {
    return uniq(flows.map((f) => f.flowOriginId));
  }, [flows]);

  const isStatusFilter = property.key === DATA_GRID_STATUS_COLUMN_KEY;

  const mergedFiltersValues = mergeFiltersValues({
    selectedFiltersValues: filtersValues,
    defaultFiltersValues,
    excludeSelectedStatusValues: isStatusFilter,
  });

  const filterProperties = filtersToGraphQLFilters({
    filters: availableFilters,
    filtersValues:
      // Prevent passing filter values for the current filter key, except for the 'currentState' filter,
      // as it needs to know about default filter status values to properly return options
      isStatusFilter ? mergedFiltersValues : omit(mergedFiltersValues, property.key),
  });

  const optionsQuery = performanceWorkFilterOptionsQuery.get({
    key: property.key,
    displayNamePath: property.displayNamePath,
    flowTypes,
    flowOriginIds,
    isDefaultProperty: property.isDefaultProperty,
    valuePaths: property.valuePaths,
    filterType: property.type!,
    filterProperties,
    additionalProperties: properties.map((p) => ({
      key: p.key,
      displayName: p.displayName,
      isExpandedProperty: false,
      valuePath: p.valuePath,
      valuePaths: p.valuePaths,
      displayNamePath: p.displayNamePath ?? null,
    })),
  });

  const { data, isFetching } = useQuery({
    queryKey: [...optionsQuery.key, filterProperties],
    queryFn: optionsQuery.fn,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    staleTime: 0,
  });

  return <PageFiltersRenderer filterProps={filterProps} data={data} isLoading={isFetching} />;
};

export const createPropertyToFilter = (storeId?: string) => {
  return (property: IProperty) => {
    return {
      property,
      renderFilter: (props: IPageFilterProps): ReactElement => {
        return <ReportingFilterRenderer filterProps={props} storeId={storeId} />;
      },
    };
  };
};

export function resolveWorksValues(works: IWork[]) {
  return works.map((work) => {
    // @ts-ignore
    work.additionalProperties?.forEach(({ key, value }: { key: string; value: any }) => {
      if (
        !isString(value) ||
        (isString(value) && !value.startsWith('$') && !value.startsWith('='))
      ) {
        return;
      }

      // @ts-ignore
      work[key] = dynamicValue(value, {
        data: work.data,
        removeNotFoundWhenReplace: true,
      });
    });

    return work;
  });
}

export function getReportingGraphQLWorksQueryKey(params: {
  reportId: string;
  graphQLFilters: string;
  isExpandByFeatureEnabled?: boolean;
}) {
  return [
    PerformanceReportKey,
    params.reportId,
    params.graphQLFilters,
    params.isExpandByFeatureEnabled,
  ];
}
